import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyGetPayRunsQuery } from '../api';
import { PayRunListItem } from '../payrun.model';

export interface CatalogItemsQueryParams {
  searchValue: string;
}

export const initialItemsQueryParamsState = {
  searchValue: null
};

export const usePaginatedGetActivePayRunsQuery = (currentPayRunId?: string) => {
  const [list, setList] = useState<PayRunListItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [queryParams, setQueryParams] = useState<CatalogItemsQueryParams>(initialItemsQueryParamsState);
  const [isMounted, setIsMounted] = useState(false);

  const [getPayRunsQuery, getPayRunsState] = useLazyGetPayRunsQuery();
  const {
    isFetching: isGetPayRunsFetching,
    isSuccess: isGetPayRunsSuccess,
    isError: isGetPayRunsError,
    // error: getPayRunsErrorState,
    data: payRunsData
  } = getPayRunsState;

  const currentTeam = useAppSelector(selectCurrentTeam);

  const page = useRef(0);
  const isInitListCall: boolean = page.current === 0;

  useEffect(() => {
    if (!isGetPayRunsFetching && isGetPayRunsSuccess) {
      const payRuns = payRunsData.payRuns ?? [];
      let filteredPayRuns = payRuns;
      if (currentPayRunId) {
        filteredPayRuns = payRuns.filter(payRun => payRun.payRunId !== currentPayRunId);
      }
      const updatedList = isInitListCall ? filteredPayRuns : [...list, ...filteredPayRuns];
      // -1 because we are not adding the current pay run to the list
      const totalCount = currentPayRunId ? payRunsData?.totalCount - 1 : payRunsData?.totalCount;
      const payRunsLeftToFetch = totalCount ? totalCount - updatedList.length : 0;

      setHasMore(payRunsLeftToFetch > 0);
      setList(updatedList);

      setIsLoading(false);
    }
  }, [isGetPayRunsFetching]);

  useEffect(() => {
    if (!isGetPayRunsFetching && isGetPayRunsError) {
      setIsLoading(false);
      setHasMore(false);
    }
  }, [isGetPayRunsError]);

  const resetListAndLoadMore = () => {
    page.current = 0;
    setList([]);
    loadMore();
  };

  useEffect(() => {
    if (isMounted) {
      resetListAndLoadMore();
    } else {
      setIsMounted(true);
    }
  }, [queryParams]);

  const loadMore = useCallback(() => {
    setIsLoading(true);
    if (currentTeam?.id) {
      try {
        getPayRunsQuery({
          teamId: currentTeam?.id,
          limit: 10,
          page: page.current,
          status: ['OPEN'],
          ...(queryParams.searchValue && { searchKey: 'payRunNumber' }),
          ...queryParams
        }).unwrap();
      } catch (err) {
        // TODO: handle error
      }
    }
    page.current = page.current + 1;
  }, [currentTeam?.id, page.current, queryParams]);

  return {
    list,
    isLoading: isLoading,
    loadMore,
    hasMore,
    setQueryParams,
    queryParams
  };
};
